import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import io from 'socket.io-client';

import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../local-storage.service';

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'error';

@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: any = null;
  private readonly connectionStatus$ = new BehaviorSubject<ConnectionStatus>('disconnected');
  private readonly eventListeners = new Map<string, Subject<any>>();
  _connect = new BehaviorSubject(false) as BehaviorSubject<boolean>;
  
  constructor(private localStorageService: LocalStorageService) {}

  /**
   * Get the current connection status as an Observable
   */
  getConnectionStatus$(): Observable<ConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  /**
   * Check if the socket is currently connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Establish connection to the Socket.IO server
   */
  connect() {
    if (!this.socket) {
      const tableId = this.localStorageService.getItem<string>('table_id');
      const idi = this.localStorageService.getItem<string>('idi');
      this.socket = io.connect(environment.socket_url, {
        transports: ['websocket'],
        autoConnect: true,
        reconnection: true,
        reconnectionDelay: 1000,
        query: {
          type: 'store',
          table: tableId,
          id: idi,
        },
      });
      this.socket.on('connect', (res: any) => {
        this._connect.next(true);
      });

      this.socket.on('disconnect', (res: any) =>
        console.log('disconnect: ' + res),
      );
      this.socket.on('error', (res: any) => console.error('error: ' + res));

      // Register any event listeners that were subscribed before the socket was created
      this.eventListeners.forEach((subject, event) => {
        this.socket.on(event, (data: any) => {
          subject.next(data);
        });
      });
    }
  }

  /**
   * Disconnect from the Socket.IO server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectionStatus$.next('disconnected');
    }
  }

  /**
   * Emit an event to the server
   * @param event The event name
   * @param data The data to send
   */
  emit<T = any>(event: string, data?: T): void {
    if (!this.socket?.connected) {
      console.warn('[SocketService] Cannot emit - not connected');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Emit an event and wait for acknowledgement
   * @param event The event name
   * @param data The data to send
   * @returns Promise that resolves with the acknowledgement response
   */
  emitWithAck<T = any, R = any>(event: string, data?: T): Promise<R> {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      this.socket.emit(event, data, (response: R) => {
        resolve(response);
      });
    });
  }

  /**
   * Listen to an event from the server
   * @param event The event name to listen to
   * @returns Observable that emits when the event is received
   */
  on<T = any>(event: string): Observable<T> {
    if (!this.eventListeners.has(event)) {
      const subject = new Subject<T>();
      this.eventListeners.set(event, subject);
      if (this.socket) {
        this.socket.on(event, (data: T) => {
          subject.next(data);
        });
      }
    }

    return this.eventListeners.get(event)!.asObservable();
  }

  /**
   * Remove a specific event listener
   * @param event The event name to stop listening to
   */
  off(event: string): void {
    if (this.socket) {
      this.socket.off(event);
    }

    const subject = this.eventListeners.get(event);
    if (subject) {
      subject.complete();
      this.eventListeners.delete(event);
    }
  }

  /**
   * Join a specific room
   * @param room The room name to join
   */
  joinRoom(room: string): void {
    this.emit('join', room);
  }

  /**
   * Leave a specific room
   * @param room The room name to leave
   */
  leaveRoom(room: string): void {
    this.emit('leave', room);
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      this.connectionStatus$.next('connected');

      // Re-register all event listeners after reconnection
      this.eventListeners.forEach((subject, event) => {
        this.socket?.on(event, (data: any) => {
          subject.next(data);
        });
      });
    });

    this.socket.on('disconnect', (reason: string) => {
      this.connectionStatus$.next('disconnected');
    });

    this.socket.on('connect_error', (error: Error) => {
      console.error('[SocketService] Connection error:', error.message);
      this.connectionStatus$.next('error');
    });

    this.socket.on('reconnect', (attemptNumber: number) => {
      this.connectionStatus$.next('connected');
    });

    this.socket.on('reconnect_attempt', (attemptNumber: number) => {
      this.connectionStatus$.next('connecting');
    });

    this.socket.on('reconnect_error', (error: Error) => {
      console.error('[SocketService] Reconnection error:', error.message);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[SocketService] Reconnection failed');
      this.connectionStatus$.next('error');
    });
  }
}

